import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GenreBook {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  cover_cache_url?: string;
  average_rating?: number;
  rating_count?: number;
  published_year?: number;
  isbn13?: string;
  isbn10?: string;
}

interface UseGenreBooksOptions {
  genre: string;
  limit?: number;
  offset?: number;
  sortBy?: 'rating' | 'popularity' | 'recent';
}

interface GenreBooksResult {
  books: GenreBook[];
  totalCount: number;
}

export function useGenreBooks({ genre, limit = 24, offset = 0, sortBy = 'popularity' }: UseGenreBooksOptions) {
  return useQuery({
    queryKey: ['genre-books', genre, limit, offset, sortBy],
    queryFn: async (): Promise<GenreBooksResult> => {
      if (!genre) {
        return { books: [], totalCount: 0 };
      }

      // Build query based on sort preference
      let query = supabase
        .from('books')
        .select('id, title, authors, cover_url, cover_cache_url, average_rating, rating_count, published_year, isbn13, isbn10', { count: 'exact' })
        .contains('genres', [genre])
        .not('duplicate_of', 'not.is', null); // Exclude duplicates

      // Apply sorting
      switch (sortBy) {
        case 'rating':
          query = query
            .order('average_rating', { ascending: false, nullsFirst: false })
            .order('rating_count', { ascending: false });
          break;
        case 'recent':
          query = query
            .order('published_year', { ascending: false, nullsFirst: false })
            .order('rating_count', { ascending: false });
          break;
        case 'popularity':
        default:
          query = query
            .order('rating_count', { ascending: false, nullsFirst: false })
            .order('average_rating', { ascending: false });
          break;
      }

      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching genre books:', error);
        throw error;
      }

      return {
        books: data || [],
        totalCount: count || 0,
      };
    },
    enabled: !!genre,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Hook to get popular authors in a genre
export function useGenreAuthors(genre: string, limit = 10) {
  return useQuery({
    queryKey: ['genre-authors', genre, limit],
    queryFn: async () => {
      if (!genre) return [];

      // Get books in this genre with their authors and ratings
      const { data: books, error } = await supabase
        .from('books')
        .select('authors, rating_count')
        .contains('genres', [genre])
        .not('authors', 'is', null)
        .order('rating_count', { ascending: false, nullsFirst: false })
        .limit(100);

      if (error) {
        console.error('Error fetching genre authors:', error);
        throw error;
      }

      // Aggregate authors by book count and total ratings
      const authorStats = new Map<string, { bookCount: number; totalRatings: number }>();

      for (const book of books || []) {
        if (!book.authors) continue;
        for (const author of book.authors) {
          if (!author) continue;
          const existing = authorStats.get(author) || { bookCount: 0, totalRatings: 0 };
          authorStats.set(author, {
            bookCount: existing.bookCount + 1,
            totalRatings: existing.totalRatings + (book.rating_count || 0),
          });
        }
      }

      // Sort by total ratings and return top authors
      const sortedAuthors = Array.from(authorStats.entries())
        .sort((a, b) => b[1].totalRatings - a[1].totalRatings)
        .slice(0, limit)
        .map(([name, stats]) => ({
          name,
          bookCount: stats.bookCount,
          totalRatings: stats.totalRatings,
        }));

      return sortedAuthors;
    },
    enabled: !!genre,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
}
